import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNotifications } from './useNotifications';
import type { Relation } from '../types/relation';
import { normalizeRelation } from '../types/relation';
import { globalCache } from './useCachedData';

export function useRelations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createNotification } = useNotifications();
  // Nous ne pouvons pas utiliser useNotifications directement ici car c'est un hook
  // Nous allons donc continuer à utiliser supabase directement pour les notifications

  const createRelation = async (relationData: Omit<Relation, 'id' | 'creator_id' | 'created_at' | 'updated_at'> & { 
    creator_name?: string;
    creator_avatar?: string;
  }) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Creating relation with data:", relationData);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error("User not authenticated");
        throw new Error('User not authenticated');
      }

      console.log("Authenticated user:", userData.user.id);

      // Adapter les données pour correspondre à la structure de la table
      // Convertir les noms de champs target_* en noms sans préfixe si nécessaire
      const adaptedData = {
        // Utiliser les noms de colonnes sans préfixe "target_"
        photo: relationData.target_photo,
        name: relationData.target_name,
        phones: relationData.target_phones,
        country: relationData.target_country,
        city: relationData.target_city,
        status: relationData.status,
        confirmed: relationData.confirmed,
        rejected: relationData.rejected,
        deleted: false,
        creator_name: relationData.creator_name,
        creator_avatar: relationData.creator_avatar,
        comment: relationData.comment
      };

      console.log("Adapted data for insertion:", adaptedData);

      // Vérifier si la table a une colonne user_id ou creator_id
      const { error: columnError } = await supabase
        .from('relations')
        .select()
        .limit(1);

      if (columnError && columnError.message.includes("user_id")) {
        console.log("La table utilise creator_id au lieu de user_id");
        
        // Insérer avec creator_id
        const { data, error } = await supabase
          .from('relations')
          .insert([
            {
              ...adaptedData,
              creator_id: userData.user.id
            }
          ])
          .select()
          .single();

        if (error) {
          console.error("Error creating relation:", error);
          throw error;
        }
        
        console.log("Relation created successfully:", data);

        // Nous ne notifions plus le créateur de sa propre action
        // L'utilisateur sait déjà qu'il a créé cette relation
        /*
        // Après avoir créé la relation avec succès, envoyer une notification
        if (data) {
          // Envoyer une notification au créateur
          await createNotification({
            userId: data.creator_id || data.user_id, // ID de l'utilisateur qui recevra la notification
            translationKey: 'new_relation', // Clé de traduction définie dans fr.ts et en.ts
            translationParams: {
              userName: relationData.creator_name || 'Vous',
              targetName: (relationData.name || relationData.target_name || 'quelqu\'un') as string
            }
          });
        }
        */
        
        return { data: normalizeRelation(data), error: null };
      } else {
        // Insérer avec user_id et creator_id
        console.log("Tentative d'insertion avec user_id et creator_id");
        
        const insertData = {
          ...adaptedData,
          user_id: userData.user.id,
          creator_id: userData.user.id,
          creator_name: relationData.creator_name,
          creator_avatar: relationData.creator_avatar
        };
        
        console.log("Final insert data:", insertData);
        
        const { data, error } = await supabase
          .from('relations')
          .insert([insertData])
          .select()
          .single();

        if (error) {
          console.error("Error creating relation:", error);
          
          // Si l'erreur est liée au champ 'relations', on continue sans ce champ
          if (error.message && error.message.includes('relations')) {
            console.log("Erreur liée au champ relations, continuons sans ce champ");
            // Pas besoin de faire une tentative alternative puisque nous avons déjà supprimé le champ relations
          } else {
            // Tentative alternative avec les données originales
            console.log("Tentative avec les données originales");
            
            const originalData = {
              target_photo: relationData.target_photo,
              target_name: relationData.target_name,
              target_phones: relationData.target_phones,
              target_country: relationData.target_country,
              target_city: relationData.target_city,
              status: relationData.status,
              confirmed: relationData.confirmed,
              rejected: relationData.rejected,
              creator_id: userData.user.id,
              user_id: userData.user.id,
              creator_name: relationData.creator_name,
              creator_avatar: relationData.creator_avatar
            };
            
            console.log("Original data insert:", originalData);
            
            const { data: origData, error: origError } = await supabase
              .from('relations')
              .insert([originalData])
              .select()
              .single();
              
            if (origError) {
              console.error("Original data insertion failed:", origError);
              throw error; // Throw the original error
            }
            
            console.log("Original data insertion succeeded:", origData);
            
            // Nous ne notifions plus le créateur de sa propre action
            // L'utilisateur sait déjà qu'il a créé cette relation
            /*
            // Après avoir créé la relation avec succès, envoyer une notification
            if (origData) {
              await createNotification({
                userId: origData.creator_id || origData.user_id,
                translationKey: 'new_relation',
                translationParams: {
                  userName: relationData.creator_name || 'Vous',
                  targetName: (relationData.target_name || 'quelqu\'un') as string
                }
              });
            }
            */
            
            return { data: normalizeRelation(origData), error: null };
          }
          
          return { data: null, error };
        }
        
        console.log("Relation created successfully:", data);

        // Nous ne notifions plus le créateur de sa propre action
        // L'utilisateur sait déjà qu'il a créé cette relation
        /*
        // Après avoir créé la relation avec succès, envoyer une notification
        if (data) {
          // Envoyer une notification au créateur
          await createNotification({
            userId: data.creator_id || data.user_id, // ID de l'utilisateur qui recevra la notification
            translationKey: 'new_relation', // Clé de traduction définie dans fr.ts et en.ts
            translationParams: {
              userName: relationData.creator_name || 'Vous',
              targetName: (relationData.name || relationData.target_name || 'quelqu\'un') as string
            }
          });
        }
        */
        
        return { data: normalizeRelation(data), error: null };
      }
    } catch (err) {
      console.error('Error creating relation:', err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const updateRelation = async (relationId: string, updates: Partial<Relation>) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Updating relation:", relationId, "with data:", updates);

      // Récupérer d'abord la relation pour vérifier si elle a une relation miroir
      const { data: relation, error: fetchError } = await supabase
        .from('relations')
        .select('*')
        .eq('id', relationId)
        .single();

      if (fetchError) {
        console.error("Error fetching relation:", fetchError);
        throw fetchError;
      }

      // Adapter les données pour correspondre à la structure de la table
      const adaptedUpdates: any = {
        updated_at: new Date().toISOString() // Toujours mettre à jour la date de modification
      };
      
      // Mapper explicitement chaque champ pour s'assurer que toutes les mises à jour sont appliquées
      // Mettre à jour à la fois les colonnes principales et les colonnes target_*
      if ('target_photo' in updates) {
        adaptedUpdates.photo = updates.target_photo;
        adaptedUpdates.target_photo = updates.target_photo;
      }
      if ('target_name' in updates && !relation.confirmed) {
        // Ne permettre la modification du nom que si la relation n'est pas confirmée
        adaptedUpdates.name = updates.target_name;
        adaptedUpdates.target_name = updates.target_name;
      }
      if ('target_phones' in updates) {
        adaptedUpdates.phones = updates.target_phones;
        adaptedUpdates.target_phones = updates.target_phones;
      }
      if ('target_country' in updates) {
        adaptedUpdates.country = updates.target_country;
        adaptedUpdates.target_country = updates.target_country;
      }
      if ('target_city' in updates) {
        adaptedUpdates.city = updates.target_city;
        adaptedUpdates.target_city = updates.target_city;
      }
      if ('status' in updates) adaptedUpdates.status = updates.status;
      if ('confirmed' in updates) adaptedUpdates.confirmed = updates.confirmed;
      if ('rejected' in updates) adaptedUpdates.rejected = updates.rejected;
      if ('deleted' in updates) adaptedUpdates.deleted = updates.deleted;
      if ('certifier_id' in updates) adaptedUpdates.certifier_id = updates.certifier_id;
      if ('mirror_relation_id' in updates) adaptedUpdates.mirror_relation_id = updates.mirror_relation_id;
      if ('creator_name' in updates) adaptedUpdates.creator_name = updates.creator_name;
      if ('creator_avatar' in updates) adaptedUpdates.creator_avatar = updates.creator_avatar;
      if ('comment' in updates) adaptedUpdates.comment = updates.comment;

      console.log("Adapted updates:", adaptedUpdates);

      const { data, error } = await supabase
        .from('relations')
        .update(adaptedUpdates)
        .eq('id', relationId)
        .select('*')  // Assurez-vous de sélectionner toutes les colonnes
        .single();

      if (error) {
        console.error("Error updating relation:", error);
        throw error;
      }
      
      console.log("Relation updated successfully:", data);
      
      // Si la relation a une relation miroir et que le statut ou la localisation a changé,
      // propager ces changements à la relation miroir
      if (data.mirror_relation_id && 
          (updates.status || updates.target_country || updates.target_city)) {
        
        const mirrorUpdates: any = {
          updated_at: new Date().toISOString()
        };
        
        if (updates.status) mirrorUpdates.status = updates.status;
        if (updates.target_country) mirrorUpdates.country = updates.target_country;
        if (updates.target_city) mirrorUpdates.city = updates.target_city;
        
        console.log("Updating mirror relation:", data.mirror_relation_id, "with data:", mirrorUpdates);
        
        const { error: mirrorError } = await supabase
          .from('relations')
          .update(mirrorUpdates)
          .eq('id', data.mirror_relation_id);
          
        if (mirrorError) {
          console.error("Error updating mirror relation:", mirrorError);
          // Ne pas bloquer le flux principal si la mise à jour du miroir échoue
        } else {
          console.log("Mirror relation updated successfully");
        }
      }
      
      // Normaliser les données avant de les retourner
      const normalizedData = normalizeRelation(data);
      console.log("Normalized data:", normalizedData);
      
      return { data: normalizedData, error: null };
    } catch (err) {
      console.error('Error updating relation:', err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const getUserRelations = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      setError(null);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error("User not authenticated");
        throw new Error('User not authenticated');
      }

      // Calculer l'offset pour la pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Récupérer les relations avec les informations des profils
      const { data, error, count } = await supabase
        .from('relations')
        .select(`
          *,
          creator:profiles!relations_creator_id_fkey(
            id,
            full_name,
            avatar_url
          ),
          certifier:profiles!relations_certifier_id_fkey(
            id,
            full_name,
            avatar_url
          )
        `, { count: 'exact' })
        .or(`creator_id.eq.${userData.user.id},certifier_id.eq.${userData.user.id}`)
        .eq('deleted', false)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error("Error fetching relations:", error);
        throw error;
      }

      console.log("Relations récupérées:", data);
      console.log("Nombre total de relations:", count);
      
      // Créer un objet pour suivre les relations déjà traitées par ID
      const processedIds = new Set();
      const processedRelations = [];

      // Filtrer explicitement les relations qui sont marquées comme supprimées
      const activeRelations = data.filter(relation => 
        relation.deleted === false && 
        relation.deleted_at === null
      );

      console.log("Relations actives après filtrage:", activeRelations.length);

      // Récupérer les IDs des relations miroir qui ont été supprimées
      const { data: deletedMirrorRelations, error: mirrorError } = await supabase
        .from('relations')
        .select('id, mirror_relation_id')
        .eq('deleted', true)
        .not('mirror_relation_id', 'is', null);
      
      const deletedMirrorIds = new Set();
      if (!mirrorError && deletedMirrorRelations) {
        // Collecter tous les IDs des miroirs et des relations originales supprimées
        deletedMirrorRelations.forEach(relation => {
          deletedMirrorIds.add(relation.id);
          if (relation.mirror_relation_id) {
            deletedMirrorIds.add(relation.mirror_relation_id);
          }
        });
      }
      
      console.log("Relations miroir supprimées:", deletedMirrorIds);

      // Filtre supplémentaire pour exclure les relations dont le miroir a été supprimé
      const finalActiveRelations = activeRelations.filter(relation => 
        !deletedMirrorIds.has(relation.id) && 
        !deletedMirrorIds.has(relation.mirror_relation_id || '')
      );
      
      console.log("Relations actives après filtrage des miroirs supprimés:", finalActiveRelations.length);

      // Traiter d'abord les relations où l'utilisateur est créateur et qui ne sont pas des miroirs
      for (const relation of finalActiveRelations) {
        // Le créateur doit uniquement voir ses relations originales (non-miroir)
        if (relation.creator_id === userData.user.id && !relation.is_mirror) {
          // Vérifier que ce n'est pas une relation avec "profile.relations.noName"
          const hasValidName = relation.name && relation.name !== 'profile.relations.noName';
          const hasValidTargetName = relation.target_name && relation.target_name !== 'profile.relations.noName';
          
          if (hasValidName || hasValidTargetName) {
            processedIds.add(relation.id);
            
            // Ne pas inclure certifier_name s'il est inconnu
            const certifierInfo = relation.certifier?.full_name ? {
              certifier_name: relation.certifier.full_name,
              certifier_avatar: relation.certifier?.avatar_url
            } : {};
            
            processedRelations.push(normalizeRelation({
              ...relation,
              ...certifierInfo,
              can_edit: true, // Le créateur peut toujours éditer sa relation, même si elle est certifiée
              is_certified: relation.confirmed,
              is_mirror: false
            }));
          }
        }
      }

      // Ensuite, traiter les relations où l'utilisateur est certifieur (via les relations miroir)
      for (const relation of finalActiveRelations) {
        // Le certifieur doit uniquement voir les relations miroir dont il est créateur
        if (relation.creator_id === userData.user.id && relation.is_mirror && !processedIds.has(relation.id)) {
          // Vérifier que ce n'est pas une relation avec "profile.relations.noName"
          const hasValidName = relation.name && relation.name !== 'profile.relations.noName';
          const hasValidTargetName = relation.target_name && relation.target_name !== 'profile.relations.noName';
          const hasValidCreatorName = relation.creator?.full_name && relation.creator?.full_name !== 'profile.relations.noName';
          
          if (hasValidName || hasValidTargetName || hasValidCreatorName) {
            processedIds.add(relation.id);
            processedRelations.push(normalizeRelation({
              ...relation,
              target_name: relation.target_name || relation.name,
              target_photo: relation.target_photo || relation.photo,
              target_phones: relation.phones || [], 
              is_mirror: true,
              can_edit: false,
              is_certified: true,
              comment: relation.comment
            }));
          }
        }
      }

      console.log("Nombre final de relations traitées:", processedRelations.length);

      const totalCount = count || 0; // Utiliser 0 si count est null

      return { 
        data: processedRelations, 
        error: null,
        totalCount,
        currentPage: page,
        hasMore: totalCount > to + 1
      };
    } catch (err) {
      console.error('Error fetching relations:', err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const deleteRelation = async (relationId: string) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Deleting relation:", relationId);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error("User not authenticated");
        throw new Error('User not authenticated');
      }

      // Récupérer la relation pour déterminer s'il s'agit d'une relation miroir
      const { data: relationData, error: relationError } = await supabase
        .from('relations')
        .select('id, creator_id, certifier_id, mirror_relation_id, photo, target_photo, name, target_name')
        .eq('id', relationId)
        .single();

      if (relationError) {
        console.error("Error fetching relation:", relationError);
        throw relationError;
      }

      console.log("Relation to delete:", relationData);
      
      // Si l'utilisateur est le certifieur d'une relation, ou le créateur d'une relation miroir,
      // nous devons supprimer la relation et sa relation miroir
      const isCertifier = relationData.certifier_id === userData.user.id;
      const isCreator = relationData.creator_id === userData.user.id;
      const isMirrorRelation = !!relationData.mirror_relation_id;

      // Vérifier que l'utilisateur est autorisé à supprimer cette relation
      if (!isCreator && !isCertifier) {
        console.error("User not authorized to delete this relation");
        throw new Error('Vous n\'êtes pas autorisé à supprimer cette relation');
      }
      
      console.log("Is creator:", isCreator, "Is certifier:", isCertifier, "Is mirror relation:", isMirrorRelation);

      // Relations à supprimer (commence avec la relation actuelle)
      let relationsToDelete = [relationId];
      
      // Si cette relation a une relation miroir, l'ajouter à la liste
      if (relationData.mirror_relation_id) {
        relationsToDelete.push(relationData.mirror_relation_id);
      }
      
      // Si cette relation est elle-même une relation miroir, nous devons trouver et ajouter la relation d'origine
      if (!isMirrorRelation) {
        // Rechercher si cette relation est référencée comme miroir par une autre relation
        const { data: originalRelation, error: originalError } = await supabase
          .from('relations')
          .select('id, creator_id, certifier_id')
          .eq('mirror_relation_id', relationId)
          .maybeSingle(); // Utiliser maybeSingle car il peut y avoir 0 ou 1 résultat
        
        if (!originalError && originalRelation) {
          console.log("Found original relation:", originalRelation);
          relationsToDelete.push(originalRelation.id);
        }
      }
      
      console.log("Relations to delete:", relationsToDelete);

      // Supprimer les fichiers et nettoyer le stockage pour chaque relation
      for (const relId of relationsToDelete) {
        try {
          // Nettoyer les fichiers de stockage
          try {
            const { error: cleanError } = await supabase
              .rpc('clean_relation_storage', { relation_id: relId });
              
            console.log(`Nettoyage du stockage pour relation ${relId}:`, cleanError ? "Échec" : "Succès");
          } catch (rpcErr) {
            console.log("Fonction RPC clean_relation_storage non disponible:", rpcErr);
            
            // Fallback pour le nettoyage de stockage si la fonction RPC n'est pas disponible
            try {
              // Récupérer les détails de la relation pour obtenir ses photos
              const { data: relDetails } = await supabase
                .from('relations')
                .select('photo, target_photo')
                .eq('id', relId)
                .single();
                
              if (relDetails) {
                // Supprimer les photos par leur URL exacte
                const photosToDelete = [relDetails.photo, relDetails.target_photo].filter(Boolean);
                
                for (const photoUrl of photosToDelete) {
                  if (!photoUrl) continue;
                  
                  try {
                    // Extraire le chemin de stockage à partir de l'URL
                    const storagePathMatch = photoUrl.match(/\/storage\/v1\/object\/public\/relations\/(.+?)(\?.*)?$/);
                    if (storagePathMatch && storagePathMatch[1]) {
                      const storagePath = decodeURIComponent(storagePathMatch[1]);
                      console.log(`Deleting photo from path: ${storagePath}`);
                      
                      const { error: photoDeleteError } = await supabase.storage
                    .from('relations')
                        .remove([storagePath]);
                    
                      if (photoDeleteError) {
                        console.error(`Error deleting photo from ${storagePath}:`, photoDeleteError);
                  } else {
                        console.log(`Photo successfully deleted from ${storagePath}`);
                      }
                    }
                  } catch (photoError) {
                    console.error("Error processing photo URL:", photoError);
                  }
                }
              }
            } catch (storageError) {
              console.error("Error cleaning storage:", storageError);
            }
          }
          
          // ÉTAPE CRUCIALE : Supprimer la relation en utilisant la fonction RPC delete_relation
          const { error: deleteError } = await supabase
            .rpc('delete_relation', { 
              relation_id: relId,
              user_id: userData.user.id 
            });

          if (deleteError) {
            console.error(`Error deleting relation ${relId}:`, deleteError);
          } else {
            console.log(`Relation ${relId} deleted successfully via RPC`);
          }
        } catch (relationError) {
          console.error(`Error processing relation ${relId}:`, relationError);
        }
      }

      // Forcer l'invalidation du cache pour toutes les relations liées
      // Vider complètement tous les caches liés aux relations
      Object.keys(globalCache).forEach(cacheKey => {
        if (cacheKey.includes('user-') || 
            cacheKey.includes('relation') || 
            cacheKey.includes('creator') || 
            cacheKey.includes('certifier')) {
          console.log(`Suppression du cache: ${cacheKey}`);
          delete globalCache[cacheKey];
        }
      });

      // Nettoyer également le localStorage et sessionStorage
      Object.keys(localStorage).forEach(key => {
        if (key.includes('relation')) {
          localStorage.removeItem(key);
        }
      });

      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('relation')) {
          sessionStorage.removeItem(key);
        }
      });

      // Pour s'assurer que les relations supprimées ne sont plus visibles, faire un appel direct
      try {
        // Suppression additionnelle directe pour s'assurer que toutes les relations sont supprimées
        for (const relId of relationsToDelete) {
          await supabase.from('relations').delete().eq('id', relId);
        }
      } catch (directDeleteError) {
        console.log("Suppression directe additionnelle (non bloquante):", directDeleteError);
      }

      // Forcer un rechargement complet des données
      await getUserRelations().then(result => {
        console.log("Relations rechargées après suppression:", result);
      });

      // Envoyer les notifications nécessaires aux utilisateurs concernés
      const notifiedUsers = new Set<string>();
      
      // Si l'utilisateur n'est pas le créateur, notifier le créateur
      if (relationData.creator_id && relationData.creator_id !== userData.user.id) {
        await createNotification({
          userId: relationData.creator_id,
          translationKey: 'relationDeletedByCertified',
          translationParams: {
            user: userData.user.email?.split('@')[0] || 'Un utilisateur',
            name: relationData.name || relationData.target_name || 'quelqu\'un'
          }
        });
        notifiedUsers.add(relationData.creator_id);
      }

      // Si l'utilisateur n'est pas le certifieur, notifier le certifieur
      if (relationData.certifier_id && relationData.certifier_id !== userData.user.id) {
        await createNotification({
          userId: relationData.certifier_id,
          translationKey: 'relationDeletedByCreator',
          translationParams: {
            user: userData.user.email?.split('@')[0] || 'Un utilisateur',
            name: relationData.name || relationData.target_name || 'quelqu\'un'
          }
        });
        notifiedUsers.add(relationData.certifier_id);
      }

      console.log("All related relations deleted successfully");
      return { error: null };
    } catch (err: any) {
      console.error('Error deleting relations:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  const certifyRelation = async (relationId: string, certifierId: string) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Début de certifyRelation avec ID:", relationId, "certifierId:", certifierId);

      // 1. Récupérer la relation avec les informations du créateur
      const { data: relation, error: fetchError } = await supabase
        .from('relations')
        .select(`
          *,
          creator:profiles!relations_creator_id_fkey(
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('id', relationId)
        .single();

      if (fetchError) {
        console.error("Error fetching relation:", fetchError);
        throw fetchError;
      }

      if (!relation) {
        throw new Error('Relation non trouvée');
      }

      // Vérifier que ce n'est pas une relation avec "profile.relations.noName"
      const hasValidName = relation.name && relation.name !== 'profile.relations.noName';
      const hasValidTargetName = relation.target_name && relation.target_name !== 'profile.relations.noName';
      const hasValidCreatorName = relation.creator?.full_name && relation.creator?.full_name !== 'profile.relations.noName';

      // Si tous les noms sont invalides, on log mais on continue quand même pour ne pas bloquer la certification
      if (!hasValidName && !hasValidTargetName && !hasValidCreatorName) {
        console.warn('Attention: Certification d\'une relation avec des noms potentiellement invalides');
      }

      // 2. Vérifier que le certifieur n'est pas le créateur
      if (relation.creator_id === certifierId) {
        throw new Error('Le créateur ne peut pas certifier sa propre relation');
      }

      // 3. SIMPLIFICATION : Mettre à jour UNIQUEMENT le statut de la relation
      // Sans toucher aux autres champs pour éviter les problèmes de contraintes
      const updateFields = {
        confirmed: true,
        rejected: false,
        certifier_id: certifierId,
        updated_at: new Date().toISOString()
      };
      
      console.log("Champs à mettre à jour:", JSON.stringify(updateFields, null, 2));

      const { error: updateError } = await supabase
        .from('relations')
        .update(updateFields)
        .eq('id', relationId);

      if (updateError) {
        console.error("Error updating relation:", updateError);
        console.error("Détails de l'erreur:", updateError.message);
        console.error("Détails complets:", JSON.stringify(updateError, null, 2));
        throw updateError;
      }

      console.log("Relation certified successfully");
      return { data: relation, error: null };
    } catch (err) {
      console.error('Error certifying relation:', err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const rejectRelation = async (relationId: string, userId: string) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Début de rejectRelation avec ID:", relationId, "userId:", userId);

      // Récupérer d'abord la relation pour vérification
      const { data: relation, error: fetchError } = await supabase
        .from('relations')
        .select('*')
        .eq('id', relationId)
        .single();

      if (fetchError) {
        console.error("Error fetching relation for rejection:", fetchError);
        throw fetchError;
      }

      console.log("Relation à rejeter:", relation);

      // Mettre à jour UNIQUEMENT le statut de la relation
      const updateFields = {
        confirmed: false,
        rejected: true,
        certifier_id: userId,
        updated_at: new Date().toISOString()
      };

      console.log("Champs à mettre à jour pour rejet:", updateFields);

      const { error: updateError } = await supabase
        .from('relations')
        .update(updateFields)
        .eq('id', relationId);

      if (updateError) {
        console.error("Error rejecting relation:", updateError);
        console.error("Détails complets:", JSON.stringify(updateError, null, 2));
        throw updateError;
      }

      // Créer une notification pour le créateur
      if (relation && relation.creator_id) {
        await createNotification({
          userId: relation.creator_id,
          translationKey: 'relation_rejected',
          translationParams: {
            name: relation.target_name || relation.name || 'quelqu\'un'
          }
        });
      }

      console.log("Relation rejected successfully");
      return { error: null };
    } catch (err) {
      console.error('Erreur lors du rejet de la relation:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createRelation,
    updateRelation,
    getUserRelations,
    deleteRelation,
    certifyRelation,
    rejectRelation
  };
}